import { DictationMic } from '@/components/shared/DictationMic'
import { useAnalytics } from '@/hooks/useAnalytics'
import { useProgressData } from '@/hooks/useProgressData'
import { useSession } from '@/hooks/useSession'
import { useSpeechToText } from '@/hooks/useSpeechToText'
import { ANSWER_TEXT_MAX_CHARS } from '@/lib/constants'
import { isProbablyMobile } from '@/lib/device'
import { cn } from '@/lib/utils'
import type { AnswerFlags, PerComponentFeedback } from '@/types/interview'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

interface TurnRecord {
  questionId: string
  questionText: string
  isFollowUp: boolean
  answer?: string
  feedback?: PerComponentFeedback
  flags?: AnswerFlags
}

export default function InterviewSessionPage() {
  const { sessionId: urlSessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()

  const {
    phase,
    sessionId,
    sessionStatus,
    currentQuestion,
    isCurrentQuestionFollowUp,
    questionIndex,
    totalQuestions,
    lastAnswer,
    error,
    resumeSession,
    submitAnswer,
  } = useSession()

  const [turns, setTurns] = useState<TurnRecord[]>([])
  const [draft, setDraft] = useState('')
  const [bannerDismissed, setBannerDismissed] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)

  const { trackSpeechToTextUsed } = useAnalytics()
  const { starAverages, loading: progressLoading } = useProgressData()
  const weaknessReminder = useMemo(() => {
    if (progressLoading || !starAverages) return null
    const components = [
      { key: 'situation', label: 'Situation', avg: starAverages.situation },
      { key: 'task', label: 'Task', avg: starAverages.task },
      { key: 'action', label: 'Action', avg: starAverages.action },
      { key: 'result', label: 'Result', avg: starAverages.result },
    ] as const
    const weakest = [...components].sort((a, b) => a.avg - b.avg)[0]
    return weakest && weakest.avg < 3.0 ? weakest : null
  }, [progressLoading, starAverages])
  const prevQuestionIdRef = useRef<string | null>(null)
  const hydratedRef = useRef(false)

  // Hydrate from the server once per mount — the resume action rebuilds the
  // answered transcript and current question, so a hard refresh or a return
  // from the dashboard restores the session instead of losing it.
  useEffect(() => {
    if (hydratedRef.current || !urlSessionId) return
    hydratedRef.current = true
    void (async () => {
      const restored = await resumeSession(urlSessionId)
      if (restored && restored.length > 0) {
        setTurns(
          restored.map((t) => ({
            questionId: t.questionId,
            questionText: t.questionText,
            isFollowUp: t.isFollowUp,
            answer: t.answer ?? undefined,
            feedback: t.feedback ?? undefined,
          }))
        )
      }
    })()
  }, [urlSessionId, resumeSession])

  // Append new turn when question changes
  useEffect(() => {
    if (!currentQuestion) return
    if (currentQuestion.id === prevQuestionIdRef.current) return
    prevQuestionIdRef.current = currentQuestion.id
    setTurns((prev) => [
      ...prev,
      {
        questionId: currentQuestion.id,
        questionText: currentQuestion.text,
        isFollowUp: isCurrentQuestionFollowUp,
      },
    ])
    setDraft('')
  }, [currentQuestion, isCurrentQuestionFollowUp])

  // Update last turn with feedback when it arrives
  useEffect(() => {
    if (!lastAnswer) return
    setTurns((prev) => {
      if (prev.length === 0) return prev
      const updated = [...prev]
      updated[updated.length - 1] = {
        // biome-ignore lint/style/noNonNullAssertion: prev.length === 0 guard above ensures this exists
        ...updated[updated.length - 1]!,
        feedback: lastAnswer.feedback,
        flags: lastAnswer.flags,
      }
      return updated
    })
  }, [lastAnswer])

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally re-runs on turns/phase to scroll to new content
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [turns, phase])

  const isComplete = phase === 'complete'
  // An abandoned session never ran completeSession(), so it has no overall_score
  // and no report — showing "Continue to Results" would send the user to a
  // FeedbackPage with nothing to display.
  const isAbandoned = sessionStatus === 'abandoned'

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault()
      const answer = draft.trim()
      if (!answer) return

      setTurns((prev) => {
        if (prev.length === 0) return prev
        const updated = [...prev]
        // biome-ignore lint/style/noNonNullAssertion: prev.length === 0 guard above ensures this exists
        updated[updated.length - 1] = { ...updated[updated.length - 1]!, answer }
        return updated
      })

      const ok = await submitAnswer(answer)

      if (!ok) {
        // Roll back the optimistic write — otherwise a failed answer keeps
        // rendering as a sent bubble, which reads as success. `draft` is left
        // alone so the composer still holds the text for a retry.
        setTurns((prev) => {
          if (prev.length === 0) return prev
          const updated = [...prev]
          // biome-ignore lint/style/noNonNullAssertion: prev.length === 0 guard above ensures this exists
          const { answer: _discarded, ...rest } = updated[updated.length - 1]!
          updated[updated.length - 1] = rest
          return updated
        })
      }
    },
    [draft, submitAnswer]
  )

  const isSubmitting = phase === 'loading'
  const canSubmit = draft.trim().length > 0 && !isSubmitting && phase === 'questioning'

  // Progress label shown in header — questionIndex is the current main question number
  const activeTotal = totalQuestions
  const activeIndex = questionIndex

  const {
    isListening,
    isSupported,
    interimTranscript,
    error: sttError,
    startListening,
    stopListening,
  } = useSpeechToText({
    onFinalTranscript: (text) => {
      setDraft((prev) => {
        const trimmed = prev.trimEnd()
        return trimmed ? `${trimmed} ${text}` : text
      })
    },
  })

  // Web Speech is unreliable on mobile — hide the mic there (see src/lib/device.ts)
  const canDictate = isSupported && !isProbablyMobile()

  const handleMicClick = useCallback(() => {
    if (isListening) {
      stopListening()
    } else {
      startListening()
      if (sessionId) {
        trackSpeechToTextUsed({ session_id: sessionId, question_sequence: activeIndex })
      }
    }
  }, [isListening, startListening, stopListening, sessionId, activeIndex, trackSpeechToTextUsed])
  const progressLabel = isCurrentQuestionFollowUp
    ? `Q${activeIndex} follow-up`
    : activeTotal > 0
      ? `Q${activeIndex} of ${activeTotal}`
      : null

  if (!urlSessionId) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center p-8 text-center space-y-4">
        <p className="text-sm text-muted-foreground">No active session found.</p>
        <Link
          to="/interview/setup"
          className="text-sm text-primary underline underline-offset-4 hover:opacity-80"
        >
          Start a new interview
        </Link>
      </main>
    )
  }

  if (phase === 'idle' || phase === 'resuming') {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center p-8">
        <p className="text-sm text-muted-foreground">Restoring your session…</p>
      </main>
    )
  }

  // Resume failed before any transcript rendered — dead-end with a way out
  if (phase === 'error' && turns.length === 0) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center p-8 text-center space-y-4">
        <p className="text-sm text-destructive">{error}</p>
        <Link
          to="/interview/setup"
          className="text-sm text-primary underline underline-offset-4 hover:opacity-80"
        >
          Start a new interview
        </Link>
      </main>
    )
  }

  return (
    <div className="flex flex-col min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-border bg-background/90 backdrop-blur-sm px-4 py-3 flex items-center justify-between">
        <Link
          to="/dashboard"
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          ← End session
        </Link>
        {progressLabel && (
          <div className="flex items-center gap-2">
            {/* Progress dots for main questions */}
            {activeTotal > 0 && !isCurrentQuestionFollowUp && (
              <div className="flex gap-1">
                {Array.from({ length: activeTotal }, (_, i) => i).map((pos) => (
                  <span
                    key={`q${pos}`}
                    className={cn(
                      'w-1.5 h-1.5 rounded-full transition-colors',
                      pos < activeIndex ? 'bg-lime' : 'bg-muted-foreground/30'
                    )}
                  />
                ))}
              </div>
            )}
            <span className="text-xs font-medium text-foreground">{progressLabel}</span>
          </div>
        )}
      </header>

      {/* Conversation */}
      <main className="flex-1 overflow-y-auto px-4 py-6 space-y-6 max-w-2xl mx-auto w-full">
        {/* Weakness reminder banner */}
        {weaknessReminder && !bannerDismissed && !isComplete && (
          <div className="flex items-start justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-800/60 dark:bg-amber-950/30 px-4 py-3 text-sm">
            <p className="text-amber-800 dark:text-amber-300 leading-snug">
              💡 Based on your recent sessions, focus on your{' '}
              <strong>{weaknessReminder.label}</strong> — avg {weaknessReminder.avg.toFixed(1)}/5.
              Try to end each answer with a specific, quantified outcome.
            </p>
            <button
              type="button"
              onClick={() => setBannerDismissed(true)}
              aria-label="Dismiss tip"
              className="flex-shrink-0 text-amber-600 dark:text-amber-400 hover:opacity-70 transition-opacity text-lg leading-none"
            >
              ×
            </button>
          </div>
        )}
        {turns.map((turn, i) => (
          <div key={turn.questionId} className="space-y-4">
            {/* Question bubble */}
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold">
                RR
              </div>
              <div className="flex-1 space-y-1">
                {turn.isFollowUp && (
                  <p className="text-xs text-muted-foreground font-medium">Follow-up</p>
                )}
                <div className="rounded-xl rounded-tl-none bg-muted px-4 py-3 text-sm leading-relaxed">
                  {turn.questionText}
                </div>
              </div>
            </div>

            {/* User answer */}
            {turn.answer && (
              <div className="flex gap-3 justify-end">
                <div className="max-w-[85%] rounded-xl rounded-tr-none bg-primary px-4 py-3 text-sm text-primary-foreground leading-relaxed">
                  {turn.answer}
                </div>
              </div>
            )}

            {/* Per-component feedback — shown between turns, and on all turns when session is complete */}
            {turn.feedback && (i < turns.length - 1 || isComplete) && (
              <FeedbackSummary feedback={turn.feedback} flags={turn.flags} />
            )}

            {/* Short-answer warning when waiting for follow-up */}
            {turn.feedback && i === turns.length - 1 && turn.flags?.shortAnswer && (
              <p className="text-xs text-amber-600 dark:text-amber-400 px-1">
                Your answer was quite brief — try to add more detail in your next response.
              </p>
            )}
          </div>
        ))}

        {/* Loading indicator */}
        {isSubmitting && (
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold">
              RR
            </div>
            <div className="rounded-xl rounded-tl-none bg-muted px-4 py-3">
              <div className="flex gap-1 items-center h-5">
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce [animation-delay:0ms]" />
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce [animation-delay:150ms]" />
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce [animation-delay:300ms]" />
              </div>
            </div>
          </div>
        )}

        {/* Abandoned session card — expired before completion, no report exists */}
        {isComplete && isAbandoned && (
          <div className="rounded-xl border border-border bg-muted/40 px-6 py-8 text-center space-y-4">
            <div className="space-y-1">
              <p className="font-semibold text-foreground">This session expired</p>
              <p className="text-sm text-muted-foreground">
                It was left inactive for too long. Your answers above were saved, but this session
                can't be continued or scored — start a new one to keep practicing.
              </p>
            </div>
            <Link
              to="/interview/setup"
              className="inline-block rounded-xl bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
            >
              Start a new interview →
            </Link>
          </div>
        )}

        {/* Session complete card */}
        {isComplete && !isAbandoned && sessionId && (
          <div className="rounded-xl border border-border bg-muted/40 px-6 py-8 text-center space-y-4">
            <div className="text-2xl">🎉</div>
            <div className="space-y-1">
              <p className="font-semibold text-foreground">Session complete!</p>
              <p className="text-sm text-muted-foreground">
                Review your answers above, then head to your results.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate(`/feedback/${sessionId}`)}
              className="rounded-xl bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
            >
              Continue to Results →
            </button>
          </div>
        )}

        {/* Error — recoverable submit failures keep phase 'questioning' and offer a retry */}
        {error && (
          <div
            role="alert"
            className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive text-center space-y-2"
          >
            <p>{error}</p>
            {phase === 'questioning' && draft.trim().length > 0 && (
              <button
                type="button"
                onClick={() => void handleSubmit()}
                className="rounded-lg border border-destructive/40 px-3 py-1.5 text-xs font-semibold hover:bg-destructive/10 transition-colors"
              >
                Retry
              </button>
            )}
          </div>
        )}

        <div ref={bottomRef} />
      </main>

      {/* Input area — hidden when session is complete */}
      {!isComplete && (
        <footer className="sticky bottom-0 border-t border-border bg-background/90 backdrop-blur-sm px-4 py-4">
          <form onSubmit={handleSubmit} className="max-w-2xl mx-auto flex gap-3 items-end">
            <div className="flex-1 flex flex-col gap-1">
              <div className="relative">
                <textarea
                  value={draft}
                  maxLength={ANSWER_TEXT_MAX_CHARS}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && canSubmit) {
                      void handleSubmit(e as unknown as React.FormEvent)
                    }
                  }}
                  placeholder={
                    canDictate
                      ? 'Type or speak your answer… (⌘↵ to submit)'
                      : 'Type your answer… (⌘↵ to submit)'
                  }
                  rows={3}
                  disabled={isSubmitting || phase === 'error'}
                  className={cn(
                    'w-full resize-none rounded-xl border border-border bg-background px-3 py-2.5 pr-10 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 transition-opacity'
                  )}
                />
                <DictationMic
                  isListening={isListening}
                  isSupported={canDictate}
                  error={sttError}
                  disabled={isSubmitting || phase === 'error'}
                  interimTranscript={interimTranscript}
                  onToggle={handleMicClick}
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-40 self-end"
            >
              Send
            </button>
          </form>
        </footer>
      )}
    </div>
  )
}

interface FeedbackSummaryProps {
  feedback: PerComponentFeedback
  flags?: AnswerFlags
}

function FeedbackSummary({ feedback, flags }: FeedbackSummaryProps) {
  const [open, setOpen] = useState(false)

  return (
    <div className="ml-11 rounded-xl border border-border bg-background text-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-medium text-muted-foreground hover:bg-muted/50 transition-colors"
      >
        <span>Feedback on previous answer</span>
        <span>{open ? '↑' : '↓'}</span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
          {flags?.shortAnswer && (
            <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">
              Answer was brief — try to add more detail.
            </p>
          )}
          {flags?.verbose && (
            <p className="text-xs text-muted-foreground font-medium">
              Answer was quite long — aim for clarity and conciseness.
            </p>
          )}
          <FeedbackRow label="Situation" text={feedback.situationFeedback} />
          <FeedbackRow label="Task" text={feedback.taskFeedback} />
          <FeedbackRow label="Action" text={feedback.actionFeedback} />
          <FeedbackRow label="Result" text={feedback.resultFeedback} />
        </div>
      )}
    </div>
  )
}

interface FeedbackRowProps {
  label: string
  text: string
}

function FeedbackRow({ label, text }: FeedbackRowProps) {
  return (
    <div className="space-y-0.5">
      <p className="text-xs font-semibold text-foreground">{label}</p>
      <p className="text-xs text-muted-foreground leading-relaxed">{text}</p>
    </div>
  )
}
